import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  HomePageLayoutBlueprint,
  HomePageWidgetBlueprint,
} from '@backstage/plugin-home-react/alpha';
import { Content } from '@backstage/core-components';
import { CustomHomepageGrid } from '@backstage/plugin-home';
import { Fragment } from 'react/jsx-runtime';
import { JSX } from 'react';
import { createAnnouncementsWidget } from './AnnouncementsWidget';
import { createOpenPullRequestsWidget } from './OpenPullRequestsWidget';
import { createRequestedReviewsWidget } from './RequestedReviewsWidget';

type CreateCardHomePageWidgetOptions = {
  extensionId: string;
  widgetName: string;
  title: string;
  description: string;
  Component: () => JSX.Element;
};

type CardHomePageWidgetRendererProps = {
  Content: () => JSX.Element;
};

function createCardHomePageWidget({
  extensionId,
  widgetName,
  title,
  description,
  Component,
}: CreateCardHomePageWidgetOptions) {
  return HomePageWidgetBlueprint.make({
    name: extensionId,
    params: {
      name: widgetName,
      title,
      description,
      components: async () => ({
        Content: Component,
      }),
      componentProps: {
        Renderer: ({
          Content: WidgetContent,
        }: CardHomePageWidgetRendererProps) => <WidgetContent />,
      },
    },
  });
}

export type CreateCardHomePageWidget = typeof createCardHomePageWidget;

const homePageLayout = HomePageLayoutBlueprint.make({
  params: {
    loader: async () =>
      // Mirrors the platform's DefaultHomePageLayout, only adding mobile-friendly
      // breakpoints/cols since page:home's config schema has no slot for those.
      function HomePageLayout({ widgets, defaultConfig }) {
        const gridConfig = defaultConfig?.map(({ column, row, ...rest }) => ({
          ...rest,
          x: column,
          y: row,
        }));
        return (
          <Content>
            <CustomHomepageGrid
              config={gridConfig}
              breakpoints={{
                xl: 1536,
                lg: 1200,
                md: 996,
                sm: 700,
                xs: 480,
                xxs: 0,
              }}
              cols={{ xl: 12, lg: 12, md: 12, sm: 1, xs: 1, xxs: 1 }}
            >
              {widgets.map((widget, index) => (
                <Fragment key={widget.name ?? index}>
                  {widget.component}
                </Fragment>
              ))}
            </CustomHomepageGrid>
          </Content>
        );
      },
  },
});

const announcementsWidget = createAnnouncementsWidget(createCardHomePageWidget);
const openPullRequestsWidget = createOpenPullRequestsWidget(
  createCardHomePageWidget,
);
const requestedReviewsWidget = createRequestedReviewsWidget(
  createCardHomePageWidget,
);

export const appModuleHome = createFrontendModule({
  pluginId: 'home',
  extensions: [
    homePageLayout,
    announcementsWidget,
    openPullRequestsWidget,
    requestedReviewsWidget,
  ],
});
